import {useEffect, useState} from "react";
import useFetchIngredients from "../shopify/useIngredientsFetch";
import {supabase} from "../api/supabase";
import {useLocale} from "../localization/useLocale";

type Ingredient = {
  imageUrl: string;
  title: string;
  amount: null | string;
};

export function useIngredients({
  productId,
  ingredientHandles,
  people,
}: {
  productId: number;
  ingredientHandles: string[];
  people: 2 | 4;
}) {
  if (!ingredientHandles || ingredientHandles.length === 0) {
    throw new Error("ingredientHandles array is not set or empty");
  }

  const {locale} = useLocale();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  const {
    data: shopifyIngredients,
    isLoading,
    error: fetchError,
  } = useFetchIngredients({ingredientHandles});

  useEffect(() => {
    if (isLoading) return;
    if (fetchError) {
      setError(fetchError);
      return;
    }

    supabase
      .schema("airtablepg_production")
      .from("fct_recipe_ingredients_with_units")
      .select("*")
      .eq("recipe_shopify_id", productId)
      .eq("language_code", locale.languageCode.toLowerCase())
      .then(({data: supabaseData}) => {
        const supabaseIngredients = supabaseData;
        const supabaseIngredientsFor2 = supabaseIngredients.filter(
          (ingredient) => ingredient.people === 2
        );
        const supabaseIngredientsFor4 = supabaseIngredients.filter(
          (ingredient) => ingredient.people === 4
        );

        const formated = supabaseIngredientsFor2.map((ingredientFor2) => {
          const {ingredient_title_et, ingredient_shopify_id} = ingredientFor2;
          const shopifyIngredient = shopifyIngredients.find(
            ({id}) => id === ingredient_shopify_id
          );
          const image = shopifyIngredient?.image || window.brokenimageUrl;

          const formatUnits = ({
            units_et,
            units,
            weight,
          }: {
            units_et: string;
            units: number;
            weight: number;
          }) => (units ? `${units_et}, ${weight}g` : null);

          const ingredientFor4 = supabaseIngredientsFor4.find(
            ({ingredient_shopify_id: id}) => id === ingredient_shopify_id
          );

          return {
            imageUrl: image,
            title: ingredient_title_et,
            unitsFor2: formatUnits(ingredientFor2),
            unitsFor4: formatUnits(ingredientFor4),
          };
        }, []);

        setData(formated);
        setLoading(false);
      });
  }, [isLoading, fetchError, shopifyIngredients]);

  const ingredients: Ingredient[] = data.map(
    ({unitsFor2, unitsFor4, ...rest}) => ({
      amount: people === 2 ? unitsFor2 : unitsFor4,
      ...rest,
    })
  );

  return {
    loading,
    error,
    ingredients,
  };
}
